import cx from 'classnames';
import { forwardRef, useState, ImgHTMLAttributes, useRef, useEffect } from 'react';

import { Box } from '../Box';

import * as styles from './Image.css';

export interface ImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  fetchpriority?: 'low' | 'high' | 'auto';
}

/**
 * This generic Image component supports lazy loading.
 */
export const Image = forwardRef<HTMLImageElement, ImageProps>(function Image(
  { className, alt, ...props }: ImageProps,
  ref
) {
  const [loading, setLoading] = useState<boolean>(true);
  const [inView, setInView] = useState(false);
  const placeholderRef = useRef<HTMLElement>();

  /**
   * We leverage IntersectionObserver to lazyload images when they come into
   * the view port. The component initially renders an empty div. The empty divs position
   * in the view port is tracked via IntersectionObserver. Once its position has entered the
   * view port, setInView is set to true and the image is loaded and rendered.
   */
  useEffect(() => {
    const observer = new IntersectionObserver((entries, obs) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      }
    }, {});

    if (placeholderRef.current) {
      observer.observe(placeholderRef?.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const loaded = () => setLoading(false);

  if (!inView) {
    return <Box ref={placeholderRef} style={{ height: props.height, width: props.width }} />;
  }

  return (
    <>
      <img
        className={cx({
          [styles.image]: true,
          [styles.image__hidden]: loading,
          [className as string]: !!className,
        })}
        ref={ref}
        alt={alt ?? ''}
        onLoad={loaded}
        {...props}
      />

      {loading && (
        <Box
          className={cx({ [styles.shimmerStyle]: true, [className as string]: !!className })}
          style={{ height: props.height, width: props.width }}
        />
      )}
    </>
  );
});
